import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { useOutletContext, useNavigate } from 'react-router-dom';
import SpinnerComponent from '../components/progressbar/SpinnerComponent.js'
import { useAuth0 } from "@auth0/auth0-react";
import { apiGet, apiPut } from "../services/AuthService.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import {
    btnGold, boxshadowHover, boxshadowUnHover,
    COLORS, backBtn
} from '../styles/goldTheme.js'

// Document Conversion CDEX Sender Codes — admin-only master list editor for
// AccountOperatorCode rows. In this domain "operator" and "sender" refer to the
// same entity, so the UI labels them as Sender while the backing column on the
// AccountOperatorCode table is still CdexOperatorCode.
//
// Page is intentionally simple — two columns:
//   • Operator Name (Sender)  — read-only display
//   • CDEX Sender Code        — 2-char editable text input; blank until populated
const OperatorCodes = props => {

    //properties
    const { getAccessTokenSilently } = useAuth0();

    //stateful properties (hooks)
    const [currentAccount, accountProfile, setAccountProfile, toggleIsShowSidebarMd, setIsShowSidebarMd] = useOutletContext();
    const navigate = useNavigate();
    const [pageStatus, setPageStatus] = useState('loading');
    const [dataStatus, setDataStatus] = useState('loading');
    const [pageErrors, setPageErrors] = useState('');
    const [dataErrors, setDataErrors] = useState('');
    const [originalOperatorCodes, setOriginalOperatorCodes] = useState([]);
    const [operatorCodes, setOperatorCodes] = useState([]);
    const [selectedOperatorIndex, setSelectedOperatorIndex] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [anyOperatorDirty, setAnyOperatorDirty] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [dataCriteria] = useState({
        skipCount: 0,
        takeCount: 50
    });
    const [rowCount, setRowCount] = useState(0);
    const companyTopRef = useRef(null);

    //initialize page
    useEffect(() => {
        (async () => {
            if (setIsShowSidebarMd)
                setIsShowSidebarMd(true);

            try {
                await populateOperatorCodes(1);
                setPageStatus('ready');
            } catch (ex) {
                setPageStatus('error');
                setPageErrors(ex.message);
                setDataStatus('error');
                setDataErrors(ex.message);
            }
        })();
    }, []);

    //populate operator codes for the current account
    const populateOperatorCodes = async (currentPage) => {
        let skipCount = (currentPage === 1 ? 0 : (currentPage - 1) * dataCriteria.takeCount);

        const currentCriteria = {
            skipCount: skipCount,
            takeCount: dataCriteria.takeCount
        };

        try {
            const response = await getOperatorCodes(currentCriteria);
            if (!response.success || !response.data || response.data.length === 0) {
                setHoveredIndex(null);
                setSelectedOperatorIndex(null);
                setSelectedOperator(null);
                setAnyOperatorDirty(false);
                setDataStatus('nodata');
                return;
            }

            setHoveredIndex(null);
            setSelectedOperatorIndex(0);
            setSelectedOperator(response.data[0]);
            setAnyOperatorDirty(false);
            setRowCount(response.rowCount);
            setOriginalOperatorCodes(response.data);
            setOperatorCodes(response.data);
            setDataStatus('ready');
        } catch (ex) {
            setDataStatus('error');
            setDataErrors(ex.message);
        }
    };

    //fetch from the AccountOperatorCode endpoint
    const getOperatorCodes = async (criteria) => {
        const apiUrl = 'accounts/' + currentAccount.id + '/operatorcodes?skipcount=' + criteria.skipCount + '&takecount=' + criteria.takeCount;
        const response = await apiGet(apiUrl, getAccessTokenSilently);
        const json = await response.json();
        return { success: response.ok, rowCount: json.rowCount, data: json.data };
    };

    //handle edits to a row's CdexOperatorCode (the CDEX Sender Code) field. Tracks
    //dirty/valid state per row so the Map button only enables when there's something
    //to save. The CDEX Sender Code is a 2-character code — enforce that limit here
    //(mirrors the same convention used by the CDEX Product Code 2-char enforcement
    //on the override page).
    const handleOperatorChange = (event) => {
        event.preventDefault();

        const fieldName = event.target.name;
        let value = event.target.value;

        if (fieldName === 'cdexOperatorCode') {
            //2-char max + auto-uppercase. CDEX sender codes are conventionally
            //2 uppercase letters (e.g. "PT", "EL"), and downstream lookups during
            //PDF parsing are case-sensitive, so normalize on input.
            if (value.length > 2) {
                value = value.substring(0, 2);
            }
            value = value.toUpperCase();
        }

        let anyDirty = false;
        let anyInvalid = false;
        const newList = [];

        operatorCodes.forEach((op, index) => {
            if (index === selectedOperatorIndex) {
                const original = originalOperatorCodes[index];
                const originalValue = original.cdexOperatorCode || '';

                const isDirty = (value !== originalValue);
                //blank is allowed (unmapped operator). Only treat as invalid if a
                //non-blank value isn't exactly 2 chars — but since the input is
                //already clamped to 2 chars above, anything 1+ length is fine. We
                //keep the flag in the shape so save logic stays parallel to JIB.
                const isValid = true;

                newList.push({
                    ...op,
                    [fieldName]: value,
                    isDirty: isDirty,
                    isValid: isValid
                });

                if (isDirty && isValid) anyDirty = true;
                if (isDirty && !isValid) anyInvalid = true;
            }
            else {
                newList.push(op);
                if (op.isDirty && op.isValid) anyDirty = true;
                if (op.isDirty && !op.isValid) anyInvalid = true;
            }
        });

        setOperatorCodes(newList);
        setAnyOperatorDirty(anyDirty && !anyInvalid);
    };

    //save all dirty rows in one PUT
    const updateOperators = async () => {
        setDataStatus('loading');
        setAnyOperatorDirty(false);

        try {
            const response = await saveOperators();
            if (response.success) {
                setAnyOperatorDirty(false);
                setOriginalOperatorCodes(response.data);
                setOperatorCodes(response.data);
                if (response.data && response.data[selectedOperatorIndex]) {
                    setSelectedOperator(response.data[selectedOperatorIndex]);
                }
                setDataStatus('ready');
            } else {
                setDataStatus('error');
                setDataErrors('Save failed.');
            }
        } catch (ex) {
            setDataStatus('error');
            setDataErrors(ex.message);
        }
    };

    const saveOperators = async () => {
        const apiUrl = 'accounts/' + currentAccount.id + '/updateoperatorcodes';
        const jsonBody = JSON.stringify(operatorCodes);
        const response = await apiPut(apiUrl, jsonBody, getAccessTokenSilently);
        const json = await response.json();
        return { success: response.ok, data: json };
    };

    //row interactions — same gold-highlight pattern used elsewhere
    const handleMouseOver = (index) => {
        if (index !== selectedOperatorIndex) setHoveredIndex(index);
    };
    const handleMouseLeave = (index) => {
        if (index !== selectedOperatorIndex) setHoveredIndex(null);
    };
    const handleMouseClick = (index) => {
        setHoveredIndex(null);
        setSelectedOperatorIndex(index);
        setSelectedOperator(operatorCodes[index]);
    };

    //render loading / error / no-data states
    const showPageLoading = () => (<div><SpinnerComponent /></div>);
    const showDataLoading = () => (<Row><SpinnerComponent /></Row>);
    const showPageError = () => (<div>Oops, there was a page load error: {pageErrors}</div>);
    const showDataError = () => (<Row>Oops, there was a data load error: {dataErrors}</Row>);

    //render the card shell + data list
    const showTableContainer = () => (
        <Card className="mb-4" style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,.10)' }}>
            <Card.Header style={{ background: '#333333', color: '#f1f1f1', borderBottom: 'none', padding: '12px 16px' }}>
                {showInputCriteria()}
            </Card.Header>
            <Card.Body style={{ paddingLeft: '12px', paddingRight: '10px' }}>
                {(() => {
                    switch (dataStatus) {
                        case 'nodata':
                            return <p>No operator codes are configured for this account.</p>
                        case 'loading':
                            return showDataLoading();
                        case 'ready':
                            return showDataMaster();
                        case 'error':
                            return showDataError();
                        default:
                            return <Row>No Data Loaded.</Row>;
                    }
                })()}
            </Card.Body>
        </Card>
    );

    //render the master list — one row per sender (operator). Two columns only:
    //Operator Name (Sender) and CDEX Sender Code.
    const showDataMaster = () => (
        <ul style={{ listStyleType: 'none', height: '515px', marginRight: '12px', overflowY: 'auto' }}>
            {operatorCodes.map((op, index) => (
                <li key={op.id || index} ref={(index === 0 ? companyTopRef : null)}
                    style={{ cursor: 'pointer', paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: '.9rem' }}
                    onMouseOver={() => handleMouseOver(index)}
                    onMouseLeave={() => handleMouseLeave(index)}
                    onClick={() => handleMouseClick(index)}>
                    <Row style={{
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: (hoveredIndex === index ? '#f3f4f6' : (selectedOperatorIndex === index ? 'rgba(37, 99, 235, 0.08)' : 'transparent')),
                        color: (hoveredIndex === index ? '#1f2937' : (selectedOperatorIndex === index ? '#2563EB' : '#374151')),
                        borderLeft: selectedOperatorIndex === index ? '3px solid #2563EB' : '3px solid transparent',
                        transition: 'background 120ms ease, color 120ms ease',
                        paddingTop: '4px',
                        paddingBottom: '4px'
                    }}>
                        <Col xs={9}><div>{op.operatorName}</div></Col>
                        <Col xs={3}>
                            <Form.Group className="mb-0" controlId={`cdexOperatorCode-${op.id || index}`}>
                                <Form.Control
                                    name="cdexOperatorCode"
                                    size="sm"
                                    aria-label="CDEX Sender Code"
                                    placeholder=""
                                    maxLength={2}
                                    value={op.cdexOperatorCode || ''}
                                    onChange={e => {
                                        //gate edits to the selected row so a stray click on a
                                        //different row's input doesn't move data around behind
                                        //the scenes
                                        if (index !== selectedOperatorIndex) setSelectedOperatorIndex(index);
                                        handleOperatorChange(e);
                                    }}
                                    onClick={e => e.stopPropagation()}
                                    style={{ fontSize: '.85rem', padding: '0px 6px', height: '24px', lineHeight: 1.2, textTransform: 'uppercase' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </li>
            ))}
        </ul>
    );

    //buttons + column headers (same dark header pattern as JIB Classification page)
    const showInputCriteria = () => (
        <Container fluid>
            <Row>
                <Col xs={5}>
                    <Button
                        size='sm'
                        style={{ ...btnGold, width: '200px' }}
                        onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                        onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                        disabled={!anyOperatorDirty}
                        onClick={anyOperatorDirty ? () => updateOperators() : null}
                    >
                        Map Sender Codes
                    </Button>
                </Col>
            </Row>
            <Row style={{ fontSize: '.9rem', marginTop: '20px', color: '#f1f1f1', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                <Col xs={9}>Operator Name (Sender)</Col>
                <Col xs={3}>CDEX Sender Code</Col>
            </Row>
        </Container>
    );

    return (
        <div>
            <Row className="align-items-center" style={{ margin: '0 0 4px 0' }}>
                <Col xs={1} style={{ marginLeft: '-7px' }}>
                    <span style={{ ...backBtn }} onClick={() => navigate('/settings')}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </span>
                </Col>
                <Col xs={10}>
                    <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#333333', margin: 0, textAlign: 'center' }}>Document Conversion CDEX Sender Codes</p>
                </Col>
                <Col xs={1}></Col>
            </Row>
            {(() => {
                switch (pageStatus) {
                    case 'loading':
                        return showPageLoading();
                    case 'ready':
                        return showTableContainer();
                    case 'error':
                        return showPageError();
                    default:
                        return <p>No Page Loaded.</p>;
                }
            })()}
        </div>
    );
};

export default OperatorCodes;
